/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { isNone } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  DEFAULT_EXPIRATION_IN_SECONDS,
  InAppExpirationType,
  InAppMessageExpiration as InAppMessageExpiration,
  Unit,
} from 'cio/utils/in-app/message-config';
import moment from 'moment-timezone';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import timeframeUnits from 'cio/utils/timeframe-units';
import { TIMEFRAME_UNITS } from 'cio/utils/round-timeframe-units';

const TYPE_OPTIONS: { label: string; value: InAppExpirationType }[] = [
  { label: 'A relative date', value: InAppExpirationType.Relative },
  { label: 'A specific date', value: InAppExpirationType.Absolute },
];

export const EXPIRATION_UNITS = ['minute', 'hour', 'day'];

interface ExpirationArgs {
  expiration?: InAppMessageExpiration;
  onChange: (expiration: InAppMessageExpiration, isAbsoluteDateSelectedOnDifferentTz?: boolean) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class Expiration extends Component<ExpirationArgs> {
  @tracked type = InAppExpirationType.Relative;

  @tracked expirationValueInSeconds: number | undefined = DEFAULT_EXPIRATION_IN_SECONDS;
  @tracked expirationDate: moment.Moment | null = null;
  @tracked relativeLiquidExpression: string | null = null;
  @tracked specificLiquidExpression: string | null = null;

  @tracked relativeLiquidUnit: Unit | undefined;

  @tracked relativeInputType: 'number' | 'liquid' = 'number';
  @tracked specificInputType: 'date' | 'liquid' = 'date';

  readonly typeOptions = TYPE_OPTIONS;
  readonly defaultUtcOffset;
  readonly timeframeUnits = EXPIRATION_UNITS;

  get availableUnits() {
    let units = this.timeframeUnits;
    return TIMEFRAME_UNITS.filter((u) => units.includes(u.value));
  }

  @tracked relativeExpirationUnitOptions = timeframeUnits({
    context: this,
    countProperty: 'expirationValueInSeconds',
    units: this.availableUnits,
  });

  constructor(owner: unknown, args: ExpirationArgs) {
    super(owner, args);

    this.defaultUtcOffset = moment().utcOffset();

    if (!isNone(args.expiration)) {
      this.type = args.expiration.type;
      let { value, isLiquidExpression, liquidExpression, liquidUnitRelative } = args.expiration;
      if (this.isRelativeDate) {
        if (isLiquidExpression) {
          this.relativeLiquidExpression = liquidExpression;
          this.relativeLiquidUnit = liquidUnitRelative;
          this.relativeInputType = 'liquid';
        } else {
          if (typeof value === 'number') {
            this.expirationValueInSeconds = value;
          }
          this.relativeInputType = 'number';
        }
      } else {
        if (isLiquidExpression) {
          this.specificLiquidExpression = liquidExpression;
          this.relativeLiquidUnit = undefined;
          this.specificInputType = 'liquid';
        } else {
          if (typeof value === 'number') {
            this.expirationDate = this.toMoment(value);
          }
          this.specificInputType = 'date';
        }
      }
    }
  }

  toMoment(timestamp?: number) {
    if (isNone(timestamp)) {
      return null;
    }
    return moment.unix(timestamp);
  }

  get errors() {
    return this.args.expiration?.errors;
  }

  get isRelativeDate() {
    return this.type === InAppExpirationType.Relative;
  }

  @action
  setExpirationType(type: InAppExpirationType) {
    this.type = type;
    if (type === InAppExpirationType.Relative) {
      this.setRelativeExpiration(this.expirationValueInSeconds);
    } else {
      let val = this.expirationDate !== null ? { date: this.expirationDate } : null;
      this.setSpecificExpiration(val);
    }
  }

  @action
  setRelativeExpiration(seconds: number | null | undefined) {
    if (!isNone(seconds)) {
      this.args.onChange(InAppMessageExpiration.relative(seconds));
    }
  }

  @action
  setSpecificExpiration(val: { date: moment.Moment } | null) {
    let date = val?.date;
    if (!isNone(date)) {
      this.expirationDate = date;
      this.args.onChange(InAppMessageExpiration.absolute(date), !this.isEquivlentTimezone(date));
    }
  }

  @action
  private isEquivlentTimezone(date: moment.Moment) {
    return date.utcOffset() === this.defaultUtcOffset;
  }

  @action
  changeSpecificInputType(val: 'date' | 'liquid') {
    this.specificInputType = val;
    if (val === 'date' && this.expirationDate) {
      this.args.onChange(
        InAppMessageExpiration.absolute(this.expirationDate),
        !this.isEquivlentTimezone(this.expirationDate),
      );
      return;
    } else if (this.specificLiquidExpression != null) {
      this.args.onChange(InAppMessageExpiration.liquid(InAppExpirationType.Absolute, this.specificLiquidExpression));
      return;
    }
  }

  @action
  changeRelativeInputType(val: 'number' | 'liquid') {
    this.relativeInputType = val;
    if (val === 'number' && this.expirationValueInSeconds != null) {
      this.args.onChange(InAppMessageExpiration.relative(this.expirationValueInSeconds));
      return;
    } else if (this.relativeLiquidExpression != null) {
      this.args.onChange(
        InAppMessageExpiration.liquid(
          InAppExpirationType.Relative,
          this.relativeLiquidExpression,
          this.relativeLiquidUnit,
        ),
      );
      return;
    }
  }

  @action
  changeRelativeLiquidUnit(unit: Unit) {
    this.relativeLiquidUnit = unit;
    if (!isNone(this.relativeLiquidExpression)) {
      this.args.onChange(
        InAppMessageExpiration.liquid(InAppExpirationType.Relative, this.relativeLiquidExpression, unit),
      );
    }
  }

  onLiquidChangeRelative = task({ restartable: true }, async (event: FlyKeyboardEvent<HTMLInputElement>) => {
    await timeout(config.APP.debounceDuration);
    if (event.target.value !== '') {
      this.relativeLiquidExpression = event.target.value;
      this.args.onChange(
        InAppMessageExpiration.liquid(InAppExpirationType.Relative, event.target.value, this.relativeLiquidUnit),
      );
    }
  });

  onLiquidChangeSpecific = task({ restartable: true }, async (event: FlyKeyboardEvent<HTMLInputElement>) => {
    await timeout(config.APP.debounceDuration);
    if (event.target.value !== '') {
      this.specificLiquidExpression = event.target.value;
      this.args.onChange(InAppMessageExpiration.liquid(InAppExpirationType.Absolute, event.target.value));
    }
  });
}
